import {
  type CurrentUserLicensesFailure,
  describeCurrentUserLicensesFailure,
} from '@ns/plugin-ns-tessa-common/current-user-licenses-loader';
import type { ChoiceState } from '@ns/plugin-picker-shared';

import {
  TESSA_USER_LICENSES_BROKEN_MESSAGE,
  TESSA_USER_LICENSES_FAILED_MESSAGE,
  TESSA_USER_LICENSES_FORBIDDEN_MESSAGE,
  TESSA_USER_LICENSES_UNAVAILABLE_MESSAGE,
  TessaUserLicensesUnavailableError,
} from './tessa-user-licenses-query';

/** Query lifecycle facts, and the field's own context, mapped into a shared picker state. */
export interface UserLicensesQueryStateInput {
  /** The caught failure, inspected to tell a permanent one from a temporary one. */
  readonly error: unknown;
  /** Whether a list with at least one license is standing, from this answer or an earlier one. */
  readonly hasItems: boolean;
  /** Whether the lookup ended in a failure. */
  readonly isError: boolean;
  /** Whether the lookup has not yet settled. */
  readonly isPending: boolean;
  /** Retries the same lookup. */
  readonly refetch?: () => void;
  /** Whether the template makes the filler choose a license. */
  readonly required: boolean;
}

/** Failure kinds that the same request will meet again. */
const PERMANENT_FAILURE_KINDS: ReadonlySet<string> = new Set([
  'parse',
  'too-large',
  'unsupported',
  'validation',
]);

/** The 4xx statuses that waiting can repair. Every other 4xx is permanent. */
const TEMPORARY_CLIENT_STATUSES: ReadonlySet<number> = new Set([408, 429]);

function isPermanent(failure: CurrentUserLicensesFailure): boolean {
  if (failure.status !== undefined) {
    return (
      failure.status >= 400 &&
      failure.status < 500 &&
      !TEMPORARY_CLIENT_STATUSES.has(failure.status)
    );
  }
  return PERMANENT_FAILURE_KINDS.has(failure.kind);
}

/** The words for a failure, and whether trying again can help. */
function readFailure(error: unknown): {
  readonly message: string;
  readonly permanent: boolean;
} {
  if (error instanceof TessaUserLicensesUnavailableError) {
    return {
      message: TESSA_USER_LICENSES_UNAVAILABLE_MESSAGE,
      permanent: true,
    };
  }
  const failure = describeCurrentUserLicensesFailure(error);
  if (failure?.status === 403) {
    return { message: TESSA_USER_LICENSES_FORBIDDEN_MESSAGE, permanent: true };
  }
  if (failure !== undefined && isPermanent(failure)) {
    return { message: TESSA_USER_LICENSES_BROKEN_MESSAGE, permanent: true };
  }
  return { message: TESSA_USER_LICENSES_FAILED_MESSAGE, permanent: false };
}

/**
 * Maps the license lookup lifecycle to a shared picker state.
 *
 * @remarks
 * It follows the two gates in the template-fields Concepts page. A failure is
 * fatal when the filler cannot get past it (the field is required and no
 * license is standing) or when nothing can put it right: the lookup is
 * switched off, the user has no access, or another permanent HTTP failure. A
 * retry is offered only when waiting can help, so also on a fatal failure of a
 * required field that a temporary failure left empty. A failure that is no
 * HTTP failure, such as a cancelled Microsoft sign-in or an unknown user,
 * counts as temporary.
 *
 * An empty answer is `ready`, not a failure: the field says so through its
 * `emptyText`.
 */
export function userLicensesQueryState(
  input: UserLicensesQueryStateInput,
): ChoiceState {
  if (input.isError) {
    const { message, permanent } = readFailure(input.error);
    const stuck = input.required && !input.hasItems;
    const retry = permanent ? undefined : input.refetch;
    return {
      kind: 'failed',
      message,
      ...(retry === undefined ? {} : { retry }),
      severity: permanent || stuck ? 'fatal' : 'warning',
    };
  }
  if (input.isPending) return { kind: 'fetching' };
  return { kind: 'ready' };
}
